import apiClient, { handleApiResponse, handleApiError } from './apiClient';

/**
 * OpenAI Service (Backend Integration)
 * Handles AI-powered features through backend API instead of direct OpenAI calls
 */
class OpenAIBackendService {
  /**
   * Analyze CV content with AI
   * @param {object} analysisParams - CV analysis parameters
   * @returns {Promise<object>} AI analysis results
   */
  async analyzeCv(analysisParams) {
    try {
      const response = await apiClient.post('/openai/analyze-cv', {
        cv_text: analysisParams.cvText,
        job_position: analysisParams.jobPosition,
        job_requirements: analysisParams.jobRequirements,
      });
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Extract key information from CV
   * @param {string} cvText - CV text content
   * @returns {Promise<object>} Extracted information
   */
  async extractCvInfo(cvText) {
    try {
      const response = await apiClient.post('/openai/extract-cv-info', {
        cv_text: cvText,
      });
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Generate interview questions
   * @param {object} questionParams - Question generation parameters
   * @returns {Promise<object>} Generated questions
   */
  async generateQuestions(questionParams) {
    try {
      const response = await apiClient.post('/openai/generate-questions', {
        candidate_summary: questionParams.candidateSummary,
        job_position: questionParams.jobPosition,
        key_skills: questionParams.keySkills || [],
      });
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Analyze interview feedback
   * @param {object} analysisParams - Feedback analysis parameters
   * @returns {Promise<object>} Analysis results
   */
  async analyzeInterview(analysisParams) {
    try {
      const response = await apiClient.post('/openai/analyze-interview', {
        interview_notes: analysisParams.interviewNotes,
        candidate_name: analysisParams.candidateName,
        position: analysisParams.position,
      });
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Check OpenAI service health
   * @returns {Promise<object>} Health status
   */
  async checkHealth() {
    try {
      const response = await apiClient.get('/openai/health');
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Batch analyze multiple CVs
   * @param {Array} cvAnalysisArray - Array of CV analysis parameters
   * @returns {Promise<Array>} Array of analysis results
   */
  async batchAnalyzeCvs(cvAnalysisArray) {
    try {
      const promises = cvAnalysisArray.map(params => this.analyzeCv(params));
      const results = await Promise.allSettled(promises);
      
      return results.map((result, index) => ({
        index,
        success: result.status === 'fulfilled',
        data: result.status === 'fulfilled' ? result.value : null,
        error: result.status === 'rejected' ? result.reason.message : null,
      }));
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Analyze candidate for specific job with comprehensive evaluation
   * @param {object} evaluationParams - Comprehensive evaluation parameters
   * @returns {Promise<object>} Detailed analysis results
   */
  async comprehensiveEvaluation(evaluationParams) {
    try {
      // First extract CV info
      const extractedInfo = await this.extractCvInfo(evaluationParams.cvText);
      
      // Then perform detailed analysis
      const analysis = await this.analyzeCv({
        cvText: evaluationParams.cvText,
        jobPosition: evaluationParams.jobPosition,
        jobRequirements: evaluationParams.jobRequirements,
      });
      
      // Generate interview questions based on analysis
      const questions = await this.generateQuestions({
        candidateSummary: analysis.analysis.ai_summary,
        jobPosition: evaluationParams.jobPosition,
        keySkills: analysis.analysis.key_skills,
      });
      
      return {
        extractedInfo: extractedInfo.extracted_info,
        analysis: analysis.analysis,
        questions: questions.questions,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Get AI insights for hiring decisions
   * @param {object} insightParams - Parameters for generating insights
   * @returns {Promise<object>} AI-generated insights
   */
  async getHiringInsights(insightParams) {
    try {
      // This combines multiple AI calls to provide comprehensive insights
      const cvAnalysis = await this.analyzeCv({
        cvText: insightParams.cvText,
        jobPosition: insightParams.jobPosition,
        jobRequirements: insightParams.jobRequirements,
      });
      
      let interviewAnalysis = null;
      if (insightParams.interviewNotes) {
        interviewAnalysis = await this.analyzeInterview({
          interviewNotes: insightParams.interviewNotes,
          candidateName: insightParams.candidateName,
          position: insightParams.jobPosition,
        });
      }
      
      return {
        cvAnalysis: cvAnalysis.analysis,
        interviewAnalysis: interviewAnalysis?.analysis,
        overallRecommendation: this.generateOverallRecommendation(
          cvAnalysis.analysis,
          interviewAnalysis?.analysis
        ),
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Generate overall recommendation based on CV and interview analysis
   * @param {object} cvAnalysis - CV analysis results
   * @param {object} interviewAnalysis - Interview analysis results (optional)
   * @returns {object} Overall recommendation
   */
  generateOverallRecommendation(cvAnalysis, interviewAnalysis = null) {
    const cvScore = cvAnalysis.overall_score || 0;
    const interviewScore = interviewAnalysis ? 
      (interviewAnalysis.overall_rating * 10) : null; // Convert to 0-100 scale
    
    let finalScore = cvScore;
    let recommendation = 'Further Review Needed';
    let confidence = 'Medium';
    
    if (interviewScore !== null) {
      // Weighted average: 60% CV, 40% Interview
      finalScore = (cvScore * 0.6) + (interviewScore * 0.4);
      confidence = 'High';
    }
    
    if (finalScore >= 80) {
      recommendation = 'Strongly Recommend';
    } else if (finalScore >= 65) {
      recommendation = 'Recommend';
    } else if (finalScore >= 50) {
      recommendation = 'Consider with Caution';
    } else {
      recommendation = 'Not Recommended';
    }
    
    return {
      finalScore: Math.round(finalScore),
      recommendation,
      confidence,
      reasoning: this.generateRecommendationReasoning(cvAnalysis, interviewAnalysis, finalScore),
    };
  }

  /**
   * Generate reasoning for recommendation
   * @param {object} cvAnalysis - CV analysis
   * @param {object} interviewAnalysis - Interview analysis
   * @param {number} finalScore - Final calculated score
   * @returns {string} Reasoning text
   */
  generateRecommendationReasoning(cvAnalysis, interviewAnalysis, finalScore) {
    const reasons = [];
    
    if (cvAnalysis.skills_match >= 80) {
      reasons.push('Strong skills alignment');
    }
    if (cvAnalysis.experience_match >= 80) {
      reasons.push('Excellent experience match');
    }
    if (interviewAnalysis && interviewAnalysis.communication_rating >= 8) {
      reasons.push('Strong communication skills');
    }
    if (interviewAnalysis && interviewAnalysis.technical_rating >= 8) {
      reasons.push('Solid technical competency');
    }
    
    if (reasons.length === 0) {
      if (finalScore < 50) {
        reasons.push('Below threshold performance in key areas');
      } else {
        reasons.push('Mixed performance across evaluation criteria');
      }
    }
    
    return reasons.join(', ');
  }
}

export default new OpenAIBackendService();